/**
 * AI Testing Panel
 * Test both Groq and Ollama services with various prompts
 */

import React, { useState } from 'react'
import { Zap, Send, Loader, CheckCircle, AlertCircle } from 'lucide-react'
import groqService from '@/services/groqService'
import ollamaService from '@/services/ollamaService'

export default function AITestingPanel({ systemStatus }) {
  const [testPrompt, setTestPrompt] = useState('Analyze this business scenario: A company has 10% monthly growth but declining customer retention. What are the key issues and recommendations?')
  const [testType, setTestType] = useState('analysis') // analysis, insights, recommendations, voice
  const [selectedService, setSelectedService] = useState('hybrid') // groq, ollama, hybrid
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState(null)
  const [error, setError] = useState(null)

  const testPrompts = {
    analysis: 'Analyze this business scenario: A company has 10% monthly growth but declining customer retention. What are the key issues and recommendations?',
    insights: 'JSON{{"revenue": 50000, "expenses": 35000, "customers": 250, "churn_rate": 0.08}}. What insights can you derive?',
    recommendations: 'Situation: Team productivity dropped 15% after remote work transition. How would you recommend addressing this?',
    voice: 'Hello! How are you today? Tell me about your capabilities in a friendly way.',
  }

  const handleRunTest = async () => {
    if (!testPrompt.trim()) {
      setError('Please enter a prompt')
      return
    }

    setLoading(true)
    setError(null)
    setResults(null)

    try {
      let response = null
      const startTime = Date.now()

      if (selectedService === 'groq' || (selectedService === 'hybrid' && systemStatus.groq.available)) {
        try {
          if (testType === 'voice') {
            response = await groqService.generateVoiceResponse(testPrompt, {})
          } else {
            response = await groqService[
              testType === 'analysis' ? 'analyzeData' :
              testType === 'insights' ? 'generateInsights' :
              'generateRecommendations'
            ](testPrompt)
          }
          const endTime = Date.now()
          setResults({
            service: 'Groq',
            response,
            duration: endTime - startTime,
            success: true,
          })
        } catch (err) {
          if (selectedService === 'groq') {
            throw err
          }
          // Fall back to Ollama in hybrid mode
          setResults(prev => ({
            ...prev,
            fallback: true,
          }))
        }
      }

      if (selectedService === 'ollama' || (selectedService === 'hybrid' && !results)) {
        try {
          if (testType === 'voice') {
            response = await ollamaService.generateVoiceResponse(testPrompt, {})
          } else {
            response = await ollamaService[
              testType === 'analysis' ? 'analyzeData' :
              testType === 'insights' ? 'generateInsights' :
              'generateRecommendations'
            ](testPrompt)
          }
          const endTime = Date.now()
          setResults({
            service: 'Ollama',
            response,
            duration: endTime - startTime,
            success: true,
            fallback: results?.fallback,
          })
        } catch (err) {
          throw err
        }
      }
    } catch (err) {
      setError(`Test failed: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Test Configuration */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-400" />
          Service Testing
        </h2>

        <div className="space-y-4">
          {/* Service Selection */}
          <div>
            <label className="text-white font-medium block mb-3">Select Service</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                {
                  value: 'groq',
                  label: 'Groq',
                  available: systemStatus.groq.available,
                  desc: 'Cloud AI',
                },
                {
                  value: 'ollama',
                  label: 'Ollama',
                  available: systemStatus.ollama.available,
                  desc: 'Local LLM',
                },
                {
                  value: 'hybrid',
                  label: 'Hybrid',
                  available: true,
                  desc: 'Intelligent routing',
                },
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setSelectedService(option.value)}
                  disabled={!option.available && option.value !== 'hybrid'}
                  className={`border-2 rounded-lg p-3 text-left transition-all ${
                    selectedService === option.value
                      ? 'border-blue-400 bg-blue-500/10'
                      : 'border-slate-600 hover:border-slate-500 bg-slate-700/20'
                  } ${!option.available && option.value !== 'hybrid' ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <p className="font-medium text-white flex items-center gap-2">
                    {option.label}
                    {option.available && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">{option.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Test Type Selection */}
          <div>
            <label className="text-white font-medium block mb-3">Test Type</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { value: 'analysis', label: 'Analysis' },
                { value: 'insights', label: 'Insights' },
                { value: 'recommendations', label: 'Recommendations' },
                { value: 'voice', label: 'Voice Response' },
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    setTestType(option.value)
                    setTestPrompt(testPrompts[option.value])
                  }}
                  className={`px-4 py-2 rounded border transition-all text-sm ${
                    testType === option.value
                      ? 'border-blue-400 bg-blue-500/20 text-white'
                      : 'border-slate-600 hover:border-slate-500 text-slate-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Prompt Input */}
          <div>
            <label className="text-white font-medium block mb-2">Test Prompt</label>
            <textarea
              value={testPrompt}
              onChange={(e) => setTestPrompt(e.target.value)}
              placeholder="Enter your test prompt..."
              rows={4}
              className="w-full bg-slate-700 border border-slate-600 rounded px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-400 resize-none"
            />
          </div>

          {/* Run Test Button */}
          <div className="flex gap-2">
            <button
              onClick={handleRunTest}
              disabled={loading || (!systemStatus.groq.available && !systemStatus.ollama.available)}
              className="flex items-center gap-2 px-6 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Running Test...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Run Test
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Test Results */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-300 font-medium">Test Failed</p>
            <p className="text-red-200 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {results && (
        <div className="bg-gradient-to-br from-emerald-900/20 to-teal-900/20 border border-emerald-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-emerald-300 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              {results.fallback ? 'Fallback Response' : `${results.service} Response`}
            </h3>
            <div className="text-sm text-slate-400">
              {results.duration}ms
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <pre className="text-slate-300 text-sm whitespace-pre-wrap font-mono overflow-x-auto max-h-96">
              {typeof results.response === 'string'
                ? results.response
                : JSON.stringify(results.response, null, 2)}
            </pre>
          </div>

          {results.fallback && (
            <div className="mt-4 bg-amber-900/30 border border-amber-500/30 rounded p-3 text-sm text-amber-300">
              Note: Primary service failed, response from fallback service
            </div>
          )}
        </div>
      )}

      {/* Service Availability Notice */}
      <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-4">
        <p className="text-sm text-slate-300">
          <strong>Service Status:</strong>
          {' '}
          {systemStatus.groq.available && systemStatus.ollama.available
            ? '✓ Both services available - hybrid mode recommended'
            : systemStatus.groq.available
            ? '✓ Only Groq available - some features limited'
            : systemStatus.ollama.available
            ? '✓ Only Ollama available - use local only'
            : '⚠ No services available - check configuration'}
        </p>
      </div>
    </div>
  )
}
